import { sum } from 'lodash-es'
import { meterToMillimeter } from '@utils'
import { kilogramToKilonewton } from 'src/utils/units'

const selectTimberFramePlot = (thickness: number, width: number) => {
  const thicknessInMeters = thickness / 1000

  if (thicknessInMeters >= width) return 'TimberFrameInsulationFull'
  if (thicknessInMeters < width && thicknessInMeters > 0) return 'TimberFrameInsulationPartly'
  else return 'TimberFrameNoInsulation'
}

const isNonSteelMaterial = (
  material: Material,
): material is LVLMemberMaterial | SoftwoodMemberMaterial | GlulamMemberMaterial => {
  return material.kind !== 'Steel'
}

const calculateCLTLayerWeight = (clt: CLTCrossSection) => {
  return clt.layers.reduce((sum, layer) => sum + layer.material.density * layer.thickness, 0)
}

const calculateTimberFrameWeight = (layer: TimberFrameLayer, isWallAssembly: boolean) => {
  const standardSection =
    'standard_beam_cs' in layer ? layer.standard_beam_cs : layer.standard_rip_cs

  if (!isNonSteelMaterial(standardSection.material)) {
    throw new Error('TimberFrameLayer cannot have a standard_rip_cs made from SteelMaterial')
  }

  const spacingFactor =
    isWallAssembly && 'standard_rip_cs' in layer
      ? 2 / 3 + 1 / layer.rip_step_size
      : 1 / layer.rip_step_size

  const memberWeight =
    standardSection.shape.width *
    standardSection.shape.height *
    spacingFactor *
    standardSection.material.density

  const insulationThickness = layer.insulation_thickness ?? 0
  const layerWeight = layer.insulation ? layer.insulation.density * insulationThickness : 0

  const totalWeight = memberWeight + layerWeight
  return totalWeight
}

const toCLTLayer = ({ clt }: CLTAssemblyLayer, earthMassForce: number): PlotLayer => ({
  layerType: 'CLT' + clt.layers.length + 'Layer',
  thickness: meterToMillimeter(calculateCumulativeThickness(clt.layers)),
  product: clt.unique_name,
  layerName: 'CLTLayer',
  density: 'n/a',
  weight: kilogramToKilonewton(calculateCLTLayerWeight(clt), earthMassForce),
})

const toProductLayer = (
  { product, thickness, color }: ProductLayer,
  earthMassForce: number,
): PlotLayer => ({
  layerType: 'Formwork',
  product: product.name,
  thickness: meterToMillimeter(thickness),
  color: color,
  density: product.density,
  layerName: 'ProductLayer',
  weight: kilogramToKilonewton(product.density * thickness, earthMassForce),
})

const toPlateLayer = ({ plate }: PlateLayer, earthMassForce: number): PlotLayer => ({
  layerType: 'GypsumBoard',
  thickness: meterToMillimeter(plate.material.thickness),
  layerName: 'PlateLayer',
  product: plate.unique_name,
  density: plate.material.density,
  weight: kilogramToKilonewton(plate.material.density * plate.material.thickness, earthMassForce),
})

const toTimberFrameLayer = (
  layer: TimberFrameLayer,
  isWallAssembly: boolean,
  earthMassForce: number,
): PlotLayer => {
  const standardSection =
    'standard_beam_cs' in layer ? layer.standard_beam_cs : layer.standard_rip_cs
  const insulationThickness = layer.insulation_thickness ?? 0

  return {
    layerType: selectTimberFramePlot(insulationThickness, standardSection.shape.width),
    thickness: meterToMillimeter(standardSection.shape.height),
    layerName: 'TimberFrameLayer',
    density: layer.insulation ? layer.insulation.density : 0,
    product: standardSection.unique_name,
    weight: kilogramToKilonewton(calculateTimberFrameWeight(layer, isWallAssembly), earthMassForce),
  }
}

export const convertToPlotLayer = (
  layer: Layer,
  isWallAssembly: boolean,
  earthMassForce: number,
): PlotLayer => {
  switch (layer.kind) {
    case 'clt-assembly-layer':
      return toCLTLayer(layer as CLTAssemblyLayer, earthMassForce)
    case 'product-layer':
      return toProductLayer(layer as ProductLayer, earthMassForce)
    case 'plate-layer':
      return toPlateLayer(layer as PlateLayer, earthMassForce)
    case 'timber-frame-layer':
    case 'timber-frame-layer-wall':
    case 'timber-frame-layer-roof-ceiling':
      return toTimberFrameLayer(layer as TimberFrameLayer, isWallAssembly, earthMassForce)
  }
}

export const calculateCumulativeThickness = (layers: PlotLayer[] | CLTLayer[]) => {
  return sum(layers.map(layer => Number(layer.thickness)))
}

const getLayerThickness = (layer: Layer): number => {
  switch (layer.kind) {
    case 'clt-assembly-layer':
      return calculateCumulativeThickness((layer as CLTAssemblyLayer).clt.layers)
    case 'product-layer':
      return (layer as ProductLayer).thickness
    case 'plate-layer':
      return (layer as PlateLayer).plate.material.thickness
    case 'timber-frame-layer':
    case 'timber-frame-layer-wall':
    case 'timber-frame-layer-roof-ceiling': {
      const timberFrameLayer = layer as TimberFrameLayer
      const standardSection =
        'standard_beam_cs' in timberFrameLayer
          ? timberFrameLayer.standard_beam_cs
          : timberFrameLayer.standard_rip_cs
      return standardSection.shape.height
    }
  }
}

/** Total thickness of an assembly in meters (sum of all layer thicknesses). */
export const calculateAssemblyThickness = (assembly: Assembly): number => {
  return sum(assembly.layers.map(getLayerThickness))
}

const STRUCTURAL_LAYER_KINDS = [
  'timber-frame-layer',
  'timber-frame-layer-wall',
  'timber-frame-layer-roof-ceiling',
  'clt-assembly-layer',
]

/**
 * Thickness of the load-bearing layer only (timber frame / CLT) in meters,
 * mirroring the backend's `assembly.structuralThickness` that the plan PDF
 * exports use for footprints and label anchors.
 */
export const calculateStructuralAssemblyThickness = (assembly: Assembly): number | undefined => {
  const structuralLayer = assembly.layers.find(layer => STRUCTURAL_LAYER_KINDS.includes(layer.kind))
  return structuralLayer && getLayerThickness(structuralLayer)
}

export const calculateCumulativeWeight = (layers: PlotLayer[]) => {
  return sum(layers.map(layer => Number(layer.weight) || 0))
}

export const generatePlottingLayers = (
  layers: Layer[],
  isWallAssembly: boolean,
  earthMassForce: number,
): PlotLayer[] => {
  const convertedLayers = layers.map(layer =>
    convertToPlotLayer(layer, isWallAssembly, earthMassForce),
  )

  return convertedLayers.filter(<T>(l: T): l is NonNullable<T> => l !== null)
}

export const calculateAssemblyWeight = (assembly: Assembly, earthMassForce: number) => {
  const layers = assembly.layers
  const isWallAssembly = assembly.usage_type === 'Outer' || assembly.usage_type === 'Inner'
  const convertedLayers = layers.map(layer =>
    convertToPlotLayer(layer, isWallAssembly, earthMassForce),
  )

  return calculateCumulativeWeight(convertedLayers)
}
